import { TZDate } from '@date-fns/tz';
import { addDays, addMinutes, format, isSameDay } from 'date-fns';
import type { ComboboxOnChangeType } from '@zillow/constellation-10';
import { Box, Button } from '@zillow/constellation-10';
import { IconPlusOutline } from '@zillow/constellation-icons';
import { isValid } from 'date-fns';
import type Constellation from '@zillow/constellation';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';
type DatePickerMetaProps = Constellation.DatePickerMetaProps;

import { type TimeZoneIds, TOUR_DURATION_NUMBER_MAP, TWENTY_FOUR_HOUR_FORMAT_STRING } from '../../constants';
import { getCalendarStepByTourDuration } from '../../utils/calendarUtils';
import TourAvailabilityCard from './components/TourAvailabilityCard';
import { isDatePastByTimeZone, isDateTooFarInAdvance, setTimeForDate } from '../../utils/formatTime';
import {
  calculateEndTimeWithPreservedDuration,
  convert12HourTimeTo24HourTime,
  isDateBeforeOrEqual,
  isOverlappingTimeSlots,
} from '../../utils/availabilityTimeUtils';
import type { AvailabilityDate, TimeSlot } from '../../types/TourAvailabilityCards';
import {
  END_TIME_BEFORE_START_TIME_ERROR_MESSAGE,
  INVALID_TIME_SELECTION_ERROR_MESSAGE,
  PAST_DATE_ERROR_MESSAGE,
  SAME_START_AND_END_TIME_ERROR_MESSAGE,
  TOO_FAR_IN_ADVANCE_ERROR_MESSAGE,
  TOUR_AVAILABILITY_CARD_OVERLAPPING_ERROR_MESSAGE,
} from '../../constants/ErrorMessageConstants';

const validateTimeSlot = (
  timeSlot: TimeSlot,
  availabilityDate: AvailabilityDate,
  currentSlotIndex: number,
  timeZone: TimeZoneIds,
): string | undefined => {
  const { start, end } = timeSlot;

  if (!start || !end) {
    return undefined;
  }

  // Validate that both dates are valid before proceeding
  if (!isValid(start) || !isValid(end)) {
    return INVALID_TIME_SELECTION_ERROR_MESSAGE;
  }

  if (isDatePastByTimeZone(start, timeZone)) {
    return PAST_DATE_ERROR_MESSAGE;
  }

  if (isDateBeforeOrEqual(end, start)) {
    if (start.getTime() === end.getTime()) {
      return SAME_START_AND_END_TIME_ERROR_MESSAGE;
    }
    return END_TIME_BEFORE_START_TIME_ERROR_MESSAGE;
  }

  if (isDateTooFarInAdvance(start, timeZone)) {
    return TOO_FAR_IN_ADVANCE_ERROR_MESSAGE;
  }

  const otherTimeSlotsOnSameDay = availabilityDate.timeSlots.filter((_, index) => index !== currentSlotIndex);
  const hasOverlap = otherTimeSlotsOnSameDay.some((otherSlot) => {
    if (!otherSlot.start || !otherSlot.end) return false;

    return isOverlappingTimeSlots({ start, end }, { start: otherSlot.start, end: otherSlot.end });
  });

  if (hasOverlap) {
    return TOUR_AVAILABILITY_CARD_OVERLAPPING_ERROR_MESSAGE;
  }

  return undefined;
};

const getNextAvailableTimeSlot = (
  targetDate: Date,
  existingTimeSlots: Array<TimeSlot>,
  timeZone: TimeZoneIds,
  tourDuration: TourDuration,
): TimeSlot | undefined => {
  const currentTime = new TZDate(new Date(), timeZone);
  const stepMinutes = getCalendarStepByTourDuration(tourDuration);
  const tourDurationMinutes = TOUR_DURATION_NUMBER_MAP[tourDuration];

  // Start from current hour + 1, or if it's a future date, start from 9 AM
  let startHour: number;

  if (isSameDay(targetDate, currentTime)) {
    // Same day - start from next hour
    startHour = currentTime.getHours() + 1;
  } else {
    // Future date - start from 9 AM
    startHour = 9;
  }

  // Find the first available non-overlapping time slot using tourDuration increments
  for (let hour = startHour; hour < 23; hour++) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      const startTime = setTimeForDate(targetDate, `${hour}:${minute.toString().padStart(2, '0')}`);
      const endTime = addMinutes(startTime, tourDurationMinutes);

      // Don't create slots that go past 23:00
      if (endTime.getHours() >= 23) {
        break;
      }

      // Check if this time slot overlaps with any existing time slots
      const hasOverlap = existingTimeSlots.some((slot) => {
        if (!slot.start || !slot.end) return false;
        return isOverlappingTimeSlots({ start: startTime, end: endTime }, { start: slot.start, end: slot.end });
      });

      if (!hasOverlap) {
        return { start: startTime, end: endTime };
      }
    }
  }

  return;
};

const formatDateToMMDDYYYY = (date: Date): string => {
  return format(date, 'MM/dd/yyyy');
};

const getNextValidAvailabilityDate = (availabilityDates: Array<AvailabilityDate>, timeZone: TimeZoneIds): string => {
  const validAvailabilityDates = availabilityDates.map((ad) => new Date(ad.date)).filter((date) => isValid(date));
  if (validAvailabilityDates.length === 0) {
    // To get the true next day for the listing, we adjust the date to the listing timezone
    // Ex: If the listing timezone is EST and client is in PST,
    // at 11pm on 1/15/2024 on the client, the time for the listing is 2am on 1/16/2024 EST, so the next should be 1/17/2024
    const currentTimeInListingTz = new TZDate(new Date(), timeZone);
    const tomorrow = addDays(currentTimeInListingTz, 1);
    return formatDateToMMDDYYYY(tomorrow);
  }

  const latestDate = new Date(Math.max(...validAvailabilityDates.map((d) => d.getTime())));
  // No need to adjust the date to the listing timezone here because we're adding 1 day relative to the latest date
  const nextDay = addDays(latestDate, 1);
  return formatDateToMMDDYYYY(nextDay);
};

type TourAvailabilityCardsProps = {
  availabilityDates: Array<AvailabilityDate>;
  onAvailabilityDatesChange: (availabilityDates: Array<AvailabilityDate>) => void;
  timeZone: TimeZoneIds;
  tourDuration?: TourDuration;
};

const TourAvailabilityCards = ({
  availabilityDates,
  onAvailabilityDatesChange,
  timeZone,
  tourDuration = TourDuration.MINUTES_30,
}: TourAvailabilityCardsProps) => {
  const validateAndUpdateAvailabilityDates = (updatedDates: Array<AvailabilityDate>) => {
    const validatedDates = updatedDates.map((availabilityDate) => ({
      ...availabilityDate,
      timeSlots: availabilityDate.timeSlots.map((timeSlot, index) => ({
        ...timeSlot,
        errorMessage: validateTimeSlot(timeSlot, availabilityDate, index, timeZone),
      })),
    }));

    onAvailabilityDatesChange(validatedDates);
  };

  const handleAddNewAvailabilityDate = () => {
    const nextAvailabilityDate = getNextValidAvailabilityDate(availabilityDates, timeZone);
    const targetDate = new Date(nextAvailabilityDate);
    const initialTimeSlot = getNextAvailableTimeSlot(targetDate, [], timeZone, tourDuration);

    if (!initialTimeSlot) {
      return;
    }

    validateAndUpdateAvailabilityDates([
      ...availabilityDates,
      {
        date: nextAvailabilityDate,
        timeSlots: [initialTimeSlot],
      },
    ]);
  };

  const getRemoveAvailabilityDateHandler = (date: string) => () => {
    const dateIndex = availabilityDates.findIndex((ad) => ad.date === date);
    if (dateIndex === -1) return;

    const updatedAvailabilityDates = [...availabilityDates];
    updatedAvailabilityDates.splice(dateIndex, 1);
    validateAndUpdateAvailabilityDates(updatedAvailabilityDates);
  };

  const getChangeDateHandler = (originalDate: string) => (date: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }

    const dateIndex = availabilityDates.findIndex((ad) => ad.date === originalDate);
    if (dateIndex === -1) return;

    const updatedAvailabilityDates = [...availabilityDates];
    updatedAvailabilityDates[dateIndex] = {
      ...updatedAvailabilityDates[dateIndex],
      date: typeof date === 'string' ? date : format(date, 'MM/dd/yyyy'),
      timeSlots: updatedAvailabilityDates[dateIndex].timeSlots.map((timeSlot) => ({
        ...timeSlot,
        start: setTimeForDate(new Date(date), format(timeSlot.start, TWENTY_FOUR_HOUR_FORMAT_STRING)),
        end: setTimeForDate(new Date(date), format(timeSlot.end, TWENTY_FOUR_HOUR_FORMAT_STRING)),
      })),
    };

    validateAndUpdateAvailabilityDates(updatedAvailabilityDates);
  };

  const getAddTimeSlotHandler = (date: string) => () => {
    const dateIndex = availabilityDates.findIndex((ad) => ad.date === date);
    if (dateIndex === -1) return;

    const updatedAvailabilityDates = [...availabilityDates];
    const hasEmptyTimeSlot = updatedAvailabilityDates[dateIndex].timeSlots.some(
      (timeslot) => timeslot.start === undefined && timeslot.end === undefined,
    );

    if (hasEmptyTimeSlot) return;

    const targetDate = new Date(date);
    const existingTimeSlots = updatedAvailabilityDates[dateIndex].timeSlots;
    const newTimeSlot = getNextAvailableTimeSlot(targetDate, existingTimeSlots, timeZone, tourDuration);

    if (!newTimeSlot) {
      return;
    }

    updatedAvailabilityDates[dateIndex] = {
      ...updatedAvailabilityDates[dateIndex],
      timeSlots: [...updatedAvailabilityDates[dateIndex].timeSlots, newTimeSlot],
    };
    validateAndUpdateAvailabilityDates(updatedAvailabilityDates);
  };

  const getChangeTimeSlotHandler =
    (date: string) =>
    (targetTimeSlot: TimeSlot, field: 'start' | 'end'): ComboboxOnChangeType<string> =>
    (value) => {
      const dateIndex = availabilityDates.findIndex((ad) => ad.date === date);
      if (dateIndex === -1) return;

      const updatedAvailabilityDates = [...availabilityDates];
      const existingTimeSlotIndex = updatedAvailabilityDates[dateIndex].timeSlots.findIndex(
        (existingTimeSlot) =>
          existingTimeSlot.start === targetTimeSlot.start && existingTimeSlot.end === targetTimeSlot.end,
      );

      if (existingTimeSlotIndex !== -1) {
        const newTime = setTimeForDate(new Date(date), convert12HourTimeTo24HourTime(value));
        const updatedTimeSlot = {
          ...updatedAvailabilityDates[dateIndex].timeSlots[existingTimeSlotIndex],
          [field]: newTime,
        };

        if (field === 'start' && targetTimeSlot.start && targetTimeSlot.end) {
          const targetDate = new Date(date);
          const newEndTime = calculateEndTimeWithPreservedDuration({
            originalStart: targetTimeSlot.start,
            originalEnd: targetTimeSlot.end,
            newStart: newTime,
            targetDate,
            tourDuration,
          });

          if (newEndTime) {
            updatedTimeSlot.end = newEndTime;
          }
        }

        updatedAvailabilityDates[dateIndex].timeSlots[existingTimeSlotIndex] = updatedTimeSlot;
      }

      validateAndUpdateAvailabilityDates(updatedAvailabilityDates);
    };

  const getRemoveTimeSlotHandler = (date: string) => (index: number) => () => {
    const dateIndex = availabilityDates.findIndex((ad) => ad.date === date);
    if (dateIndex === -1) return;

    const updatedAvailabilityDates = [...availabilityDates];
    updatedAvailabilityDates[dateIndex] = {
      ...updatedAvailabilityDates[dateIndex],
      timeSlots: updatedAvailabilityDates[dateIndex].timeSlots.filter((_, i) => i !== index),
    };
    validateAndUpdateAvailabilityDates(updatedAvailabilityDates);
  };

  return (
    <div>
      {availabilityDates.map((availabilityDate) => {
        const otherSelectedDates = availabilityDates
          .filter((otherSelectedDate) => otherSelectedDate.date !== availabilityDate.date)
          .map((otherSelectedDate) => otherSelectedDate.date);

        return (
          <Box key={availabilityDate.date} css={{ marginBottom: 'default' }}>
            <TourAvailabilityCard
              availabilityDate={availabilityDate}
              onChangeDate={getChangeDateHandler(availabilityDate.date)}
              onRemoveDate={getRemoveAvailabilityDateHandler(availabilityDate.date)}
              onAddTimeSlot={getAddTimeSlotHandler(availabilityDate.date)}
              onChangeTimeSlot={getChangeTimeSlotHandler(availabilityDate.date)}
              onRemoveTimeSlot={getRemoveTimeSlotHandler(availabilityDate.date)}
              disabledDates={otherSelectedDates}
              tourDuration={tourDuration}
            />
          </Box>
        );
      })}
      <Button
        emphasis="outlined"
        icon={<IconPlusOutline />}
        iconPosition="start"
        size="md"
        tone="brand"
        onClick={handleAddNewAvailabilityDate}
        data-testid="TourAvailabilityCards-AddAvailabilityDateButton"
      >
        Add a day
      </Button>
    </div>
  );
};

export default TourAvailabilityCards;
